import { Link } from "@tanstack/react-router";
import { Facebook, Instagram, MapPin, Mail, Phone, Clock } from "lucide-react";
import logo from "@/assets/logo-bottega.webp";
import { useRecipesHidden, useProductsHidden } from "@/hooks/useSiteSettings";
import { ObfuscatedEmailText } from "./ObfuscatedEmail";
import { apriPreferenzeCookie } from "@/lib/consent";
import { COMPANY } from "@/data/company";

// Social URLs — svuota (stringa vuota o null) per nascondere l'icona.
const SOCIALS = {
  facebook: "https://www.facebook.com/labottegadellecarnidamarco/",
  instagram: "https://www.instagram.com/labottegadellecarnidamarcosrls/",
  whatsapp: "https://wa.me/393407487310",
} as const;

function ZofiaSignature() {
  const brand = "Zofia Digital Agency";
  return (
    <span className="inline-flex items-baseline gap-1.5">
      <span className="text-brand-cream/60">Cucinato da</span>
      <a
        href="https://zofiadigitalagency.com"
        target="_blank"
        rel="noreferrer"
        aria-label={brand}
        className="zofia-signature group relative inline-block font-display tracking-wide text-brand-gold"
      >
        {brand.split("").map((ch, i) => (
          <span
            key={i}
            className="zofia-letter inline-block"
            style={{ transitionDelay: `${i * 35}ms`, animationDelay: `${i * 90}ms` }}
          >
            {ch === " " ? "\u00A0" : ch}
          </span>
        ))}
        <span aria-hidden className="absolute left-0 -bottom-0.5 h-px w-0 bg-gradient-to-r from-brand-gold via-brand-red to-brand-gold transition-all duration-500 group-hover:w-full" />
      </a>
    </span>
  );
}

export function Footer() {
  const recipesHidden = useRecipesHidden();
  const productsHidden = useProductsHidden();
  return (
    <footer className="bg-brand-charcoal text-brand-cream">
      {/* pb-24: il pulsante WhatsApp fisso in basso a destra non copre la riga dei dati societari. */}
      <div className="container mx-auto px-4 pt-16 pb-24">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-10">
          <div className="min-w-0">
            <img src={logo} alt="" width={900} height={259} loading="lazy" decoding="async" className="h-28 mb-4 brightness-0 invert opacity-90" />
            <p className="text-sm text-brand-cream/80 max-w-xs leading-relaxed">
              Nel cuore del Molise, a Campobasso: bottega indipendente di pregiate carni e prodotti selezionati.
            </p>
            <div className="flex gap-3 mt-5">
              {SOCIALS.facebook && (
                <a href={SOCIALS.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="w-10 h-10 rounded-full border border-brand-cream/20 flex items-center justify-center hover:bg-[#1877F2] hover:border-[#1877F2] transition-colors">
                  <Facebook size={16} />
                </a>
              )}
              {SOCIALS.instagram && (
                <a href={SOCIALS.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="w-10 h-10 rounded-full border border-brand-cream/20 flex items-center justify-center hover:bg-gradient-to-br hover:from-[#F58529] hover:to-[#DD2A7B] hover:border-transparent transition-colors">
                  <Instagram size={16} />
                </a>
              )}
              {SOCIALS.whatsapp && (
                <a href={SOCIALS.whatsapp} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" className="w-10 h-10 rounded-full border border-brand-cream/20 flex items-center justify-center hover:bg-[#25D366] hover:border-[#25D366] transition-colors">
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M17.5 14.4c-.3-.1-1.7-.8-2-.9-.3-.1-.5-.1-.6.1-.2.3-.7.9-.9 1.1-.2.2-.3.2-.6.1-.3-.1-1.2-.4-2.3-1.4-.9-.8-1.4-1.7-1.6-2-.2-.3 0-.5.1-.6.1-.1.3-.3.4-.5.1-.2.2-.3.2-.5.1-.2 0-.4 0-.5-.1-.1-.6-1.5-.8-2-.2-.5-.4-.4-.6-.4h-.5c-.2 0-.5.1-.7.4-.2.3-.9.9-.9 2.2 0 1.3 1 2.6 1.1 2.7.1.2 1.9 2.9 4.6 4 .7.3 1.2.5 1.6.6.7.2 1.3.2 1.8.1.5-.1 1.7-.7 1.9-1.4.2-.7.2-1.2.2-1.4-.1-.1-.3-.2-.5-.3z"/></svg>
                </a>
              )}
            </div>
          </div>

          <div className="min-w-0">
            <h4 className="font-display text-lg mb-4">Naviga</h4>
            <ul className="space-y-2 text-sm text-brand-cream/80">
              <li><Link to="/" className="hover:text-brand-gold">Home</Link></li>
              <li><Link to="/chi-siamo" className="hover:text-brand-gold">Chi siamo</Link></li>
              {!productsHidden && (
                <li><Link to="/prodotti" className="hover:text-brand-gold">Prodotti</Link></li>
              )}
              {!recipesHidden && (
                <li><Link to="/ricette" className="hover:text-brand-gold">Ricette</Link></li>
              )}
              <li><Link to="/abatina" className="hover:text-brand-gold">L'Abatina</Link></li>
              <li><Link to="/contatti" className="hover:text-brand-gold">Contatti</Link></li>
              <li><Link to="/richieste" className="hover:text-brand-gold">Richieste</Link></li>
            </ul>
          </div>

          <div className="min-w-0">
            <h4 className="font-display text-lg mb-4">Contatti</h4>
            <ul className="space-y-4 text-sm text-brand-cream/80 break-words">
              <li className="flex gap-3 items-start">
                <span className="w-9 h-9 rounded-full bg-brand-red/20 text-brand-gold flex items-center justify-center shrink-0"><MapPin size={15} /></span>
                <span>Via J. F. Kennedy, 1<br/>86100 Campobasso (CB)</span>
              </li>
              <li>
                <a href="tel:+393407487310" className="flex gap-3 items-center group">
                  <span className="w-9 h-9 rounded-full bg-brand-red/20 text-brand-gold flex items-center justify-center shrink-0 group-hover:bg-brand-red group-hover:text-white transition-colors"><Phone size={15} /></span>
                  <span className="group-hover:text-brand-gold transition-colors">+39 340 748 7310</span>
                </a>
              </li>
              <li className="flex gap-3 items-start">
                <span className="w-9 h-9 rounded-full bg-brand-red/20 text-brand-gold flex items-center justify-center shrink-0"><Mail size={15} /></span>
                <ObfuscatedEmailText className="min-w-0 break-all" />
              </li>
              <li className="flex gap-3 items-start">
                <span className="w-9 h-9 rounded-full bg-brand-gold/20 text-brand-gold flex items-center justify-center shrink-0"><Clock size={15} /></span>
                <span>Lun-Sab 8:00-13:30 / 16:00-20:00<br/>Domenica chiuso (su prenotazione)</span>
              </li>
            </ul>
          </div>

          <div className="min-w-0">
            <h4 className="font-display text-lg mb-4">Riconoscimenti</h4>
            <ul className="space-y-3 text-sm text-brand-cream/80">
              <li>Slow Food — Arca del Gusto</li>
              <li>F.I.M. Federazione Italiana Macellai</li>
              <li>De.Co. Comune di Campobasso</li>
              <li>Filiera Molisana Tracciata</li>
            </ul>
          </div>
        </div>

        <div className="gold-divider my-10 opacity-50" />

        <div className="grid grid-cols-1 md:grid-cols-3 items-center gap-4 text-xs text-brand-cream/60">
          <div className="text-center md:text-left">
            © {new Date().getFullYear()} {COMPANY.ragioneSociale} · P.&nbsp;IVA&nbsp;{COMPANY.piva}
          </div>
          <div className="text-center order-first md:order-none">
            <ZofiaSignature />
          </div>
          <div className="flex flex-wrap gap-x-5 gap-y-2 justify-center md:justify-end">
            <Link to="/privacy" className="hover:text-brand-gold">Privacy</Link>
            <Link to="/cookie" className="hover:text-brand-gold">Cookie</Link>
            <button type="button" onClick={apriPreferenzeCookie} className="hover:text-brand-gold">
              Preferenze cookie
            </button>
          </div>
        </div>
        {/* Dati societari (art. 2250 c.c. e D.Lgs. 70/2003 art. 7), fonte: VIES e scheda registro imprese, verificati il 29/10/2026. */}
        <p className="mt-6 text-center md:text-left text-xs leading-relaxed text-brand-cream/70">
          <strong className="font-medium text-brand-cream/90">{COMPANY.ragioneSociale}</strong> · Sede legale: {COMPANY.sedeLegale} · P.&nbsp;IVA e C.F. {COMPANY.piva} ·
          Registro delle Imprese del Molise n. {COMPANY.piva} · REA {COMPANY.rea} · Capitale sociale {COMPANY.capitale} i.v.
        </p>
      </div>
    </footer>
  );
}
